import {difference, isEmpty, map, sortBy} from 'lodash';
import {useCallback, useEffect, useMemo, useState, Fragment} from 'react';
import {useTranslation} from 'react-i18next';

import {userApi} from 'api';
import {ConfirmModal, PageHeader, ActionButton, notifier, DataWrapper} from 'components';
import {
    ANDROID_INTEGRATION_LOGO_PATH, APPOINTMENTS_APPLICATION_INTEGRATION_LOGO_PATH, INTEGRATION_NO_LOGO_PATH,
    IOS_INTEGRATION_LOGO_PATH, SALESFORCE_INTEGRATION_LOGO_PATH
} from 'constants/images';
import {DEVICE_INTEGRATION_SOURCES, SALESFORCE_INTEGRATION_SOURCES} from 'constants/integrations';
import {AUTH_SOURCE_TRANSLATE} from 'constants/translate';
import {EDITABLE_INTEGRATION_SOURCES} from 'constants/user';
import {AuthSource} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, User} from 'types';
import {userAuthUtils} from 'utils';

import {IntegrationDetailsModal} from './IntegrationDetailsModal';
import {IntegrationsAccordion} from './IntegrationsAccordion';
import {IntegrationsCard} from './IntegrationsCard';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.INTEGRATIONS;

type SourceModalProps = {
    open: boolean;
    source?: AuthSource;
}

type LoadingState = {
    loading?: boolean;
    error?: ApiError;
}

export const Integrations = () => {
    const {t} = useTranslation();
    const [detailsModalProps, setDetailsModalProps] = useState<SourceModalProps>({
        open: false
    });

    const [confirmDeleteModalProps, setConfirmDeleteModalProps] = useState<SourceModalProps>({
        open: false
    });

    const [setupIntegrationLoadingState, setSetupIntegrationLoadingState] = useState<LoadingState>({
        loading: false, error: null
    });

    const user = useMemo(() => userAuthUtils.getLocalUser(), []);
    const getUserFn = useCallback(() => userApi.getUser(user.eid), [user.eid]);

    const {
        send: fetchProfile,
        data: profile = null,
        loading: profileLoading
    } = useApiData<User>(getUserFn);

    const addButtonDisabled = useMemo(() => {
        const noAvailableIntegrations = isEmpty(difference(
            EDITABLE_INTEGRATION_SOURCES,
            map(profile?.integrations, (integration) => integration.source))
        );
        return !profile || profileLoading || noAvailableIntegrations;
    }, [profile, profileLoading]);

    const deleteIntegration = useCallback(async (source: AuthSource) => {
        try {
            await userApi.removeIntegration(source);
            await fetchProfile();
        } catch {
            notifier.error({details: t(T_KEYS.MESSAGES.ERROR.DELETE)});
        }
    }, [fetchProfile, t]);

    const onCloseConfirmDeleteModal = useCallback((isConfirmed: boolean) => {
        if (isConfirmed) {
            deleteIntegration(confirmDeleteModalProps.source);
        }
        setConfirmDeleteModalProps({open: false});
    }, [confirmDeleteModalProps, deleteIntegration]);

    const onCloseDetailsModal = useCallback(async (value, data) => {
        if (value) {
            try {
                setSetupIntegrationLoadingState({loading: true});
                await userApi.setIntegration(data.source, data.credentials);
                await fetchProfile();
                setDetailsModalProps({open: false});
                setSetupIntegrationLoadingState({loading: false});
            } catch (error: any) {
                setSetupIntegrationLoadingState({loading: false, error});
                notifier.error({details: t(T_KEYS.MESSAGES.ERROR.SETUP)});
            }
        } else {
            setSetupIntegrationLoadingState({error: null});
            setDetailsModalProps({open: false});
        }
    }, [fetchProfile, setSetupIntegrationLoadingState, setDetailsModalProps, t]);

    const onClickAddNewIntegration = useCallback(() => {
        setDetailsModalProps({open: true});
    }, [setDetailsModalProps]);

    const handleUpdateClick = useCallback((source: AuthSource) => {
        setDetailsModalProps({open: true, source});
    }, [setDetailsModalProps]);

    const sortedIntegrations = useMemo(() => sortBy(profile?.integrations, 'source'), [profile]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    return (
        <div className='vs-integrations-page'>
            <div className='tw-mb-4'>
                <PageHeader title={t(T_KEYS.TITLE)} description={t(T_KEYS.DESCRIPTION)} />
                <ActionButton action='create' disabled={addButtonDisabled} onClick={onClickAddNewIntegration}>
                    {t(T_KEYS.ADD_BTN)}
                </ActionButton>
            </div>

            <DataWrapper isLoading={setupIntegrationLoadingState.loading} noData={!sortedIntegrations?.length}>
                <div className='sm:tw-grid -xl:tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-3'>
                    {map(sortedIntegrations, (integration) => {
                        const isSalesforceIntegration = SALESFORCE_INTEGRATION_SOURCES.includes(integration.source);
                        const isDeviceIntegration = DEVICE_INTEGRATION_SOURCES.includes(integration.source);
                        const isAppointmentsIntegration = integration.source === AuthSource.AppointmentsApplication;
                        const isEditable = EDITABLE_INTEGRATION_SOURCES.includes(integration.source);
                        const sourceLabel = AUTH_SOURCE_TRANSLATE[integration.source] ?
                            t(AUTH_SOURCE_TRANSLATE[integration.source]) :
                            integration.source;

                        let logo = isSalesforceIntegration ? SALESFORCE_INTEGRATION_LOGO_PATH : INTEGRATION_NO_LOGO_PATH;
                        if (isDeviceIntegration) {
                            logo = integration.source === AuthSource.AndroidSDK ? ANDROID_INTEGRATION_LOGO_PATH : IOS_INTEGRATION_LOGO_PATH;
                        }
                        if (isAppointmentsIntegration) {
                            logo = APPOINTMENTS_APPLICATION_INTEGRATION_LOGO_PATH;
                        }

                        const props = {
                            logo,
                            sourceLabel,
                            isEditable,
                            isDeviceIntegration,
                            isSalesforceIntegration,
                            isAppointmentsIntegration,
                            integration,
                            onEdit: () => handleUpdateClick(integration.source),
                            onDelete: () => setConfirmDeleteModalProps({open: true, source: integration.source})
                        };

                        return (
                            <Fragment key={integration.source}>
                                <IntegrationsAccordion {...props} />
                                <IntegrationsCard {...props} />
                            </Fragment>
                        );
                    })}
                </div>
            </DataWrapper>

            <IntegrationDetailsModal
                {...detailsModalProps}
                user={profile}
                loading={setupIntegrationLoadingState.loading}
                error={setupIntegrationLoadingState.error}
                onClose={onCloseDetailsModal}
            />

            <ConfirmModal
                open={confirmDeleteModalProps.open}
                title={t(T_KEYS.CONFIRM_DELETE)}
                onClose={onCloseConfirmDeleteModal}
            />
        </div>
    );
};